import React from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";

import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";

import Navbar from "../../components/Homepage/Navbar/Navbar";
import "./Home.css";
import Footer from "../../components/Homepage/Footer/Footer";

const loginOptions = [
  {
    title: "Student Login",
    role: "STUDENT",
    description:
      "View your academic profile, attendance, fee details, library books and hostel information.",
    icon: <SchoolOutlinedIcon />,
    color: "#2463c5",
    background: "#eaf2ff",
  },
  {
    title: "HOD / Department",
    role: "HOD",
    description:
      "Register students, maintain department records and upload monthly subject-wise attendance.",
    icon: <GroupsOutlinedIcon />,
    color: "#138568",
    background: "#e4f7ef",
  },
  {
    title: "Principal Office",
    role: "PRINCIPAL_OFFICE",
    description:
      "Manage college fee demands, record payments and maintain student fee receipts.",
    icon: <AccountBalanceOutlinedIcon />,
    color: "#7650c8",
    background: "#f0eaff",
  },
  {
    title: "Library Login",
    role: "LIBRARY",
    description:
      "Manage book records, issue books, record returns and monitor outstanding books.",
    icon: <MenuBookOutlinedIcon />,
    color: "#c56b1a",
    background: "#fff0df",
  },
  {
    title: "Hostel Login",
    role: "HOSTEL",
    description:
      "Maintain hostel allocations, student accommodation, fee payments and receipts.",
    icon: <HotelOutlinedIcon />,
    color: "#138568",
    background: "#e4f7ef",
  },
  {
    title: "University Administrator",
    role: "ADMIN",
    description:
      "Manage user accounts, colleges, departments, permissions and university reports.",
    icon: <AdminPanelSettingsOutlinedIcon />,
    color: "#455b78",
    background: "#eaf0f7",
  },
];

export default function Home() {
  const navigate = useNavigate();

  const openLogin = (role) => {
  navigate(`/student-erp/login?role=${encodeURIComponent(role)}`);
};

  return (
    <Box className="erp-home">
      <Navbar />

      {/* Hero section */}
      <Box className="erp-hero">
        <Container maxWidth="xl">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box className="erp-hero-content">
                <Typography className="erp-eyebrow">
                  STUDENT INFORMATION MANAGEMENT SYSTEM
                </Typography>

                <Typography component="h1" className="erp-hero-title">
                  One University.
                  <br />
                  One Portal.
                </Typography>

                <Box className="erp-title-line" />

                <Typography className="erp-hero-description">
                  Access your academic records, attendance, fee details,
                  library and hostel services through a single university
                  platform.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  className="erp-hero-actions"
                >
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardRoundedIcon />}
                    onClick={() => openLogin("STUDENT")}
                    className="erp-primary-button"
                  >
                    Student Login
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() =>
                      document
                        .getElementById("erp-login-options")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="erp-secondary-button"
                  >
                    Official Logins
                  </Button>
                </Stack>

                <Grid container spacing={2} className="erp-feature-row">
                  <Grid item xs={4}>
                    <Box className="erp-feature">
                      <SchoolOutlinedIcon />
                      <Typography>Academic Records</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box className="erp-feature">
                      <AutoStoriesOutlinedIcon />
                      <Typography>Attendance & Fees</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box className="erp-feature">
                      <MenuBookOutlinedIcon />
                      <Typography>Library & Hostel</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box className="erp-hero-visual">
                <Box className="erp-visual-orbit erp-orbit-one" />
                <Box className="erp-visual-orbit erp-orbit-two" />

                <Box className="erp-campus-card">
                  <Box className="erp-campus-icon">
                    <AccountBalanceOutlinedIcon />
                  </Box>

                  <Typography className="erp-campus-label">
                    RAYALASEEMA UNIVERSITY
                  </Typography>

                  <Typography className="erp-campus-heading">
                    Your academic journey,
                    <br />
                    connected in one place.
                  </Typography>

                  <Divider className="erp-campus-divider" />

                  <Stack spacing={1.5}>
                    {[
                      "Student academic records",
                      "Attendance and fee information",
                      "Library and hostel services",
                    ].map((item) => (
                      <Box className="erp-campus-point" key={item}>
                        <Box className="erp-point-check">✓</Box>
                        <Typography>{item}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box className="erp-floating-badge">
                  <SchoolOutlinedIcon />
                  <Box>
                    <Typography className="erp-badge-title">
                      Student ERP
                    </Typography>
                    <Typography className="erp-badge-subtitle">
                      University services
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Login section */}
      <Box className="erp-login-section" id="erp-login-options">
        <Container maxWidth="xl">
          <Box className="erp-section-heading">
            <Typography className="erp-eyebrow">
              ACCESS YOUR ACCOUNT
            </Typography>

            <Typography component="h2" className="erp-section-title">
              Choose Your Login
            </Typography>

            <Box className="erp-title-line erp-centered-line" />

            <Typography className="erp-section-description">
              Select your role to continue to the appropriate university
              portal. Each account has access only to its authorized features.
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {loginOptions.map((option) => (
              <Grid item xs={12} sm={6} lg={4} key={option.role}>
                <Card className="erp-login-card" elevation={0}>
                  <CardContent className="erp-login-card-content">
                    <Box
                      className="erp-login-icon"
                      sx={{
                        color: option.color,
                        backgroundColor: option.background,
                      }}
                    >
                      {option.icon}
                    </Box>

                    <Typography component="h3" className="erp-card-title">
                      {option.title}
                    </Typography>

                    <Typography className="erp-card-description">
                      {option.description}
                    </Typography>

                    <Button
                      fullWidth
                      variant="outlined"
                      endIcon={<ArrowForwardRoundedIcon />}
                      onClick={() => openLogin(option.role)}
                      className="erp-card-button"
                    >
                      Login to portal
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Colleges section */}
      <Box className="erp-colleges-section">
        <Container maxWidth="xl">
          <Box className="erp-colleges-panel">
            <Box className="erp-section-heading erp-colleges-heading">
              <Typography className="erp-eyebrow">
                OUR INSTITUTIONS
              </Typography>

              <Typography component="h2" className="erp-section-title">
                University Colleges
              </Typography>

              <Box className="erp-title-line erp-centered-line" />
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Box className="erp-college-card">
                  <Box className="erp-college-icon">
                    <AccountBalanceOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography className="erp-college-title">
                      University College
                    </Typography>
                    <Typography className="erp-college-description">
                      Student records and academic administration.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className="erp-college-card">
                  <Box className="erp-college-icon">
                    <ApartmentOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography className="erp-college-title">
                      Engineering College
                    </Typography>
                    <Typography className="erp-college-description">
                      Engineering student records and academic services.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
}